import type { fabric } from 'fabric';
import type { FabricEditor } from 'modules/fabric-editor';
import { getKindProfile } from './kind-profiles';
import type { CapturedFillGradient, CapturedStyle, StyleCategoryId, StyleField } from './types';

export interface ApplyStyleResult {
  /** How many targets were offered the style. */
  targets: number;
  /** How many targets actually received at least one field. */
  touched: number;
}

/**
 * Apply the captured style onto each target, restricted to `categoryIds`.
 * Kind-aware: each target's own profile decides which captured keys it can
 * accept and where they are written (Fabric prop vs `obj.data`), so cross-kind
 * paste transfers exactly the intersection of field keys. Placeholder targets
 * are refit after their data changes. The whole paste runs inside
 * `history.batch`, producing a single undo step.
 */
export function applyStyle(
  editor: FabricEditor,
  targets: fabric.Object[],
  captured: CapturedStyle,
  categoryIds: StyleCategoryId[],
): ApplyStyleResult {
  const run = (): ApplyStyleResult => {
    let touched = 0;
    for (const target of targets) {
      const profile = getKindProfile(target);
      if (!profile) continue;

      let changed = false;
      let wroteData = false;
      for (const categoryId of categoryIds) {
        const fields: StyleField[] | undefined = profile[categoryId];
        const bucket = captured.values[categoryId];
        if (!fields || !bucket) continue;

        // Handle the gradient field first and on its own: the façade both derives
        // the `fill` prop and writes `data.fillGradient` (the source of truth the
        // inspector's checkbox reads) in one call. Route it through `setProp`
        // instead and the target would receive the source's live `fabric.Gradient`
        // object by reference, with no `data.fillGradient` — the checkbox stays
        // off and the plain fill field renders the object as "[object Object]".
        // Called with `undefined` when the source had no gradient, it also clears
        // any gradient already on the target so a plain-fill paste doesn't leave a
        // stale `data.fillGradient` behind.
        const gradientField = fields.find((f) => f.storage === 'fillGradient');
        if (gradientField) {
          const config = bucket[gradientField.key] as CapturedFillGradient | undefined;
          editor.fillGradient?.set(target, config);
          changed = true;
        }

        for (const field of fields) {
          if (field.storage === 'fillGradient') continue; // handled above
          if (!(field.key in bucket)) continue;
          const value = bucket[field.key];
          if (field.storage === 'prop') {
            editor.objects.setProp(target, field.key as keyof fabric.Object, value as never);
          } else {
            editor.objects.setData(target, field.key, value);
            wroteData = true;
          }
          changed = true;
        }
      }

      if (!changed) continue;
      touched++;
      // Placeholder visuals derive from data — rebuild the group. refit's own
      // transaction nests inside the surrounding batch (shared suspend counter).
      if (editor.placeholders?.isPlaceholder(target)) {
        editor.placeholders.refit(target as fabric.Group);
      } else if (wroteData) {
        // Plain cached objects (e.g. a native rect receiving `data.cornerRadii`)
        // do not repaint on a data-only write — invalidate the cache and redraw.
        target.dirty = true;
        editor.canvas.requestRenderAll();
      }
    }
    return { targets: targets.length, touched };
  };

  return editor.history ? editor.history.batch(run) : run();
}
